import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getMyRoles,
  getRoleCategories,
  updateMyRole,
} from '@/services/roles.ts';
import { useEffect, useState } from 'react';
import { SwitchButton } from '@/components/molecules/SwitchButton';
import { useToast } from '@/hooks/useToastContext.tsx';
import { Loader } from '@/components/atoms/Loader';

export const Role = () => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const {
    data: roleCategories,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['roleCategories'],
    queryFn: getRoleCategories,
  });

  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null
  );

  const selectedCategory = roleCategories?.find(
    (category) => category.roleCategoryId === selectedCategoryId
  );

  useEffect(() => {
    if (!selectedCategoryId) {
      setSelectedCategoryId(roleCategories?.[0]?.roleCategoryId ?? null);
    }
  });

  const { data: myRoles } = useQuery({
    queryKey: ['myRoles'],
    queryFn: getMyRoles,
  });

  const updateRole = async (roleId: string) => {
    const isAdd =
      myRoles?.find((myRole) => myRole.roleId === roleId) === undefined;

    try {
      await updateMyRole(roleId, isAdd);
      showToast('success', `Rôle ${isAdd ? 'ajouté' : 'retiré'} avec succès !`);
      await queryClient.invalidateQueries({ queryKey: ['myRoles'] });
    } catch {
      showToast(
        'error',
        'Une erreur est survenue lors de la mise à jour du rôle'
      );
    }
  };

  if (isLoading) {
    return (
      <div className="bg-page flex min-h-screen items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-page flex min-h-screen items-center justify-center">
        <p className="text-secondary-500">
          Une erreur est survenue lors du chargement des données
        </p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full flex-col md:flex-row">
      <div className="bg-surface border-b border-line-soft p-4 shadow-md md:w-84.25 md:shrink-0 md:border-r md:border-b-0 md:p-6 md:shadow-2xl">
        <div className="flex gap-2 overflow-x-auto md:flex-col md:overflow-visible">
          {roleCategories?.map((category) => (
            <SwitchButton
              key={category.roleCategoryId}
              label={category.name}
              isActive={selectedCategoryId === category.roleCategoryId}
              onClick={() => setSelectedCategoryId(category.roleCategoryId)}
              color={category.color}
            />
          ))}
        </div>
      </div>

      <div className="bg-page flex-1 p-4 sm:p-6">
        {selectedCategory ? (
          <>
            <h2 className="text-content font-lexend mb-6 text-xl font-semibold sm:text-2xl">
              {selectedCategory.name}
            </h2>

            <div className="space-y-3">
              {selectedCategory.roles.map((role) => (
                <SwitchButton
                  key={role.roleId}
                  label={role.name}
                  isActive={
                    myRoles?.some((myRole) => myRole.roleId === role.roleId) ??
                    false
                  }
                  onClick={() => updateRole(role.roleId)}
                  color={role.color}
                />
              ))}
            </div>
          </>
        ) : (
          <p className="text-content/50">
            Sélectionnez une catégorie pour voir les rôles
          </p>
        )}
      </div>
    </div>
  );
};
